import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";
import { MultiSectionDisplay } from "@/components/MultiSectionDisplay";
import { RepurposeModal } from "@/components/RepurposeModal";
import { GenerationErrorBlock } from "@/components/GenerationErrorBlock";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AnimatePresence, motion } from "framer-motion";
import { FileUploadZone } from "@/components/FileUploadZone";
import { toast } from "sonner";
import { Gift, Lightning, ArrowsClockwise } from "@phosphor-icons/react";
import { TemplateManager } from "@/components/TemplateManager";
import { UsageWarningBanner } from "@/components/UsageWarningBanner";
import { UpgradeModal } from "@/components/UpgradeModal";
import { AlwaysIncludeIndicator } from "@/components/AlwaysIncludeIndicator";
import { useLocalDraft } from "@/hooks/useLocalDraft";
import { LEAD_MAGNET_TYPES, LEAD_MAGNET_TONES, LEAD_MAGNET_LOADING_MESSAGES } from "@/components/leadmagnet/leadmagnetConstants";

export default function LeadMagnetBuilderPage() {
  const { api, refreshUser } = useAuth();
  const location = useLocation();
  const { restore, save: saveDraft, clear: clearDraft } = useLocalDraft("nib_draft_leadmagnet");

  const [titleIdea, setTitleIdea] = useState("");
  const [leadMagnetType, setLeadMagnetType] = useState("mini-guide");
  const [targetProblem, setTargetProblem] = useState("");
  const [targetOutcome, setTargetOutcome] = useState("");
  const [researchText, setResearchText] = useState("");
  const [audience, setAudience] = useState("");
  const [cta, setCta] = useState("");
  const [offerContext, setOfferContext] = useState("");
  const [toneVariations, setToneVariations] = useState(["Direct", "Practical"]);
  const [sectionCount, setSectionCount] = useState(4);
  const [files, setFiles] = useState([]);
  const [uploadedFileIds, setUploadedFileIds] = useState([]);
  const [aiModel, setAiModel] = useState("");
  const [availableModels, setAvailableModels] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [result, setResult] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [repurposeOpen, setRepurposeOpen] = useState(false);
  const [alwaysIncludeFiles, setAlwaysIncludeFiles] = useState([]);
  const [generationError, setGenerationError] = useState(null);
  const [autoRetryCount, setAutoRetryCount] = useState(0);

  useEffect(() => {
    if (location.state?.prefill) return;
    const resumeDraft = location.state?.resumeDraft;
    if (resumeDraft) {
      const ctx = resumeDraft.generation_context || {};
      if (ctx.title_idea || ctx.topic) setTitleIdea(ctx.title_idea || ctx.topic);
      if (ctx.lead_magnet_type) setLeadMagnetType(ctx.lead_magnet_type);
      if (ctx.target_problem) setTargetProblem(ctx.target_problem);
      if (ctx.target_outcome) setTargetOutcome(ctx.target_outcome);
      if (ctx.research_text) setResearchText(ctx.research_text);
      if (ctx.audience) setAudience(ctx.audience);
      if (ctx.cta) setCta(ctx.cta);
      if (ctx.offer_context) setOfferContext(ctx.offer_context);
      if (ctx.tone_variations?.length) setToneVariations(ctx.tone_variations);
      if (ctx.section_count) setSectionCount(ctx.section_count);
      if (ctx.ai_model) setAiModel(ctx.ai_model);
      if (resumeDraft.sections && Object.keys(resumeDraft.sections).length > 0) {
        setResult({ sections: resumeDraft.sections, section_names: Object.keys(resumeDraft.sections) });
      }
      return;
    }
    const draft = restore();
    if (draft) {
      if (draft.titleIdea) setTitleIdea(draft.titleIdea);
      if (draft.leadMagnetType) setLeadMagnetType(draft.leadMagnetType);
      if (draft.targetProblem) setTargetProblem(draft.targetProblem);
      if (draft.targetOutcome) setTargetOutcome(draft.targetOutcome);
      if (draft.researchText) setResearchText(draft.researchText);
      if (draft.audience) setAudience(draft.audience);
      if (draft.cta) setCta(draft.cta);
      if (draft.offerContext) setOfferContext(draft.offerContext);
      if (draft.toneVariations?.length) setToneVariations(draft.toneVariations);
      if (draft.sectionCount) setSectionCount(draft.sectionCount);
      if (draft.aiModel) setAiModel(draft.aiModel);
    }
  }, [restore]);

  useEffect(() => {
    const timer = setTimeout(() => {
      saveDraft({ titleIdea, leadMagnetType, targetProblem, targetOutcome, researchText, audience, cta, offerContext, toneVariations, sectionCount, aiModel });
    }, 500);
    return () => clearTimeout(timer);
  }, [titleIdea, leadMagnetType, targetProblem, targetOutcome, researchText, audience, cta, offerContext, toneVariations, sectionCount, aiModel, saveDraft]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const resp = await api.get("/models/available");
        setAvailableModels(resp.data.models || []);
        if (resp.data.models?.length > 0) setAiModel(resp.data.models[0]);
      } catch (e) { console.error("Failed to load model defaults:", e); }
    };
    fetchModels();
  }, [api]);

  useEffect(() => {
    const loadDefaults = async () => {
      try {
        const resp = await api.get("/profile");
        const p = resp.data;
        setAudience(prev => prev || p.default_audience || "");
        setToneVariations(prev => prev.length > 0 ? prev : (p.default_tone_variations || []));
        setAiModel(prev => prev || p.default_ai_model || "");
        setLeadMagnetType(prev => prev === "mini-guide" ? (p.default_lead_magnet_type || prev) : prev);
        setSectionCount(prev => prev === 4 ? (p.default_section_count || prev) : prev);
      } catch (e) { console.error("Failed to load model defaults:", e); }
    };
    loadDefaults();
  }, [api]);

  useEffect(() => {
    const prefill = location.state?.prefill;
    if (prefill) {
      setTitleIdea(prefill.topic || prefill.title || "");
      setResearchText(prefill.research_material || prefill.research_text || "");
      setAudience(prefill.audience || "");
      setCta(prefill.cta === "NONE" ? "" : prefill.cta || "");
      setToneVariations(prefill.tone_variations || ["Direct", "Practical"]);
      if (prefill.ai_model_used) setAiModel(prefill.ai_model_used);
      if (prefill.duplicate) toast.success("Configuration duplicated — ready to regenerate");
    }
  }, [location.state]);

  useEffect(() => {
    if (!generating) return;
    let idx = 0;
    setLoadingMsg(LEAD_MAGNET_LOADING_MESSAGES[0]);
    const interval = setInterval(() => {
      idx = (idx + 1) % LEAD_MAGNET_LOADING_MESSAGES.length;
      setLoadingMsg(LEAD_MAGNET_LOADING_MESSAGES[idx]);
    }, 4000);
    return () => clearInterval(interval);
  }, [generating]);

  const toggleTone = (tone) => {
    setToneVariations((prev) => prev.includes(tone) ? prev.filter((t) => t !== tone) : [...prev, tone]);
  };

  const handleGenerate = async (e) => {
    if (e) e.preventDefault();
    if (!titleIdea || !targetProblem || !targetOutcome || !audience || !aiModel) {
      toast.error("Please fill in all required fields"); return;
    }
    setGenerating(true); setResult(null); setIsSaved(false); setGenerationError(null); setAutoRetryCount(0);

    const fileIds = [];
    for (const file of files) {
      try {
        const formData = new FormData(); formData.append("file", file);
        const resp = await api.post("/upload", formData);
        fileIds.push(resp.data.file_id);
      } catch { toast.error(`Could not read ${file.name}.`); fileIds.push(null); }
    }
    setUploadedFileIds(fileIds);

    try {
      const resp = await api.post("/leadmagnet/build", {
        title_idea: titleIdea, lead_magnet_type: leadMagnetType,
        target_problem: targetProblem, target_outcome: targetOutcome,
        research_text: researchText, audience, cta: cta || "NONE",
        offer_context: offerContext || "NONE", tone_variations: toneVariations,
        section_count: sectionCount, ai_model: aiModel, file_ids: fileIds,
      });
      setResult(resp.data); clearDraft();
      toast.success("Lead magnet built! +30 XP"); refreshUser();
    } catch (err) {
      const data = err.response?.data;
      if (data?.error && data?.error_type) {
        setGenerationError(data);
        if (data.error_type === "rate_limit") setShowUpgrade(true);
      } else {
        if (err.response?.status === 429) setShowUpgrade(true);
        toast.error(data?.detail || "Lead magnet generation failed.");
      }
    } finally { setGenerating(false); }
  };

  const handleSave = async (mergedSections) => {
    if (!result) return;
    const sectionsToSave = mergedSections || result.sections;
    const fullDraft = result.section_names.map((name) => `### ${name}\n\n${sectionsToSave[name] || ""}`).join("\n\n---\n\n");
    await api.post("/history", {
      title: `Lead Magnet: ${titleIdea.substring(0, 60)}`, topic: titleIdea,
      research_material: researchText, content_type: "Lead Magnet", platform: "Lead Magnet (PDF)",
      audience, goal: "Generate leads", cta: cta || "NONE", offer_asset: offerContext || "NONE",
      tone_variations: toneVariations, length_constraint: "FULL LEAD MAGNET", ai_model_used: aiModel,
      generated_core_angle: targetOutcome, generated_draft: fullDraft,
      generated_why_it_works: `Complete ${leadMagnetType} lead magnet`,
      generated_alternate_hooks: [], generated_cta_variations: null,
    });
    setIsSaved(true); refreshUser();
  };

  return (
    <NoBusinessGuard>
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div className="flex items-center gap-3">
          <Gift size={28} className="text-[#FF4400]" />
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight" data-testid="leadmagnet-page-title">Lead Magnet Builder</h1>
            <p className="text-sm text-[var(--text-muted)] mt-0.5">Build a complete lead magnet -- mini-guide, checklist, framework, script, or assessment</p>
          </div>
        </div>

        <UsageWarningBanner />

        <form onSubmit={handleGenerate} className="space-y-6" data-testid="leadmagnet-form">
          <div>
            <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-3">Lead Magnet Type <span className="text-[#FF4400]">*</span></label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="leadmagnet-type-grid">
              {LEAD_MAGNET_TYPES.map((type) => (
                <button key={type.value} type="button" onClick={() => setLeadMagnetType(type.value)} data-testid={`leadmagnet-type-${type.value}`}
                  className={`px-3 py-3 rounded-md text-left border transition-all duration-200 ${leadMagnetType === type.value ? "bg-[var(--accent-subtle)] border-[#FF4400] text-[#FF4400]" : "bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]"}`}>
                  <div className="text-sm font-medium">{type.label}</div>
                  <div className="text-[10px] opacity-60 mt-0.5">{type.description}</div>
                </button>
              ))}
            </div>
          </div>

          <FormField label="Title Concept" required>
            <Input value={titleIdea} onChange={(e) => setTitleIdea(e.target.value)} placeholder="e.g., The Busy Founder's Content Audit" maxLength={255} required className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="leadmagnet-title-input" />
          </FormField>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Problem It Solves" required>
              <Textarea value={targetProblem} onChange={(e) => setTargetProblem(e.target.value)} placeholder="What specific problem does this address?" required className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] min-h-[90px] resize-y" data-testid="leadmagnet-problem-input" />
            </FormField>
            <FormField label="Desired Outcome" required>
              <Textarea value={targetOutcome} onChange={(e) => setTargetOutcome(e.target.value)} placeholder="What can they do after reading this?" required className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] min-h-[90px] resize-y" data-testid="leadmagnet-outcome-input" />
            </FormField>
          </div>

          <FormField label="Research / Source Material">
            <Textarea value={researchText} onChange={(e) => setResearchText(e.target.value)} placeholder="Paste any notes, data, frameworks..." maxLength={50000} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] min-h-[120px] resize-y" data-testid="leadmagnet-research-input" />
            <div className="mt-3"><FileUploadZone files={files} onFilesChange={setFiles} uploadedFileIds={uploadedFileIds} /></div>
          </FormField>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Audience" required>
              <Input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="e.g., agency owners, coaches, freelancers" required className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="leadmagnet-audience-input" />
            </FormField>
            <FormField label="Number of Core Sections">
              <Select value={String(sectionCount)} onValueChange={(v) => setSectionCount(Number(v))}>
                <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="leadmagnet-sections-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
                  {[3, 4, 5, 6, 7].map((n) => (<SelectItem key={n} value={String(n)} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">{n} sections</SelectItem>))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="CTA / Next Step">
              <Input value={cta} onChange={(e) => setCta(e.target.value)} placeholder="e.g., Book a free strategy call" className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="leadmagnet-cta-input" />
            </FormField>
            <FormField label="Offer Context">
              <Input value={offerContext} onChange={(e) => setOfferContext(e.target.value)} placeholder="e.g., Strategy Sprint ($5K), 90-Day Growth Program" className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="leadmagnet-offer-input" />
            </FormField>
          </div>

          <ToneSelector tones={LEAD_MAGNET_TONES} selected={toneVariations} onToggle={toggleTone} prefix="leadmagnet" />
          <ModelSelector models={availableModels} value={aiModel} onChange={setAiModel} prefix="leadmagnet" />

          <TemplateManager templateType="leadmagnet" currentValues={{ content_type: "Lead Magnet", platform: "Lead Magnet (PDF)", audience, goal: "Generate leads", cta: cta || "NONE", offer_asset: offerContext || "NONE", tone_variations: toneVariations, ai_model: aiModel, lead_magnet_type: leadMagnetType, section_count: sectionCount }}
            onLoad={(t) => { if (t.audience) setAudience(t.audience); setCta(t.cta === "NONE" ? "" : t.cta || ""); setOfferContext(t.offer_asset === "NONE" ? "" : t.offer_asset || ""); if (t.tone_variations?.length) setToneVariations(t.tone_variations); if (t.lead_magnet_type) setLeadMagnetType(t.lead_magnet_type); if (t.section_count) setSectionCount(t.section_count); if (t.ai_model && availableModels.includes(t.ai_model)) setAiModel(t.ai_model); }} />

          <AlwaysIncludeIndicator onFilesLoaded={setAlwaysIncludeFiles} />

          <AnimatePresence>
            {generationError && (<GenerationErrorBlock error={generationError} onRetry={() => handleGenerate()} onDismiss={() => { setGenerationError(null); setAutoRetryCount(0); }} autoRetryCount={autoRetryCount} onAutoRetryStart={() => setAutoRetryCount((c) => c + 1)} />)}
          </AnimatePresence>

          <button type="submit" disabled={generating || availableModels.length === 0} className="w-full bg-[#FF4400] hover:bg-[#FF6633] text-white font-semibold h-14 rounded-md text-base transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(255,68,0,0.4)] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="leadmagnet-submit-button">
            {generating ? (<><Gift size={20} className="animate-pulse" />{loadingMsg}</>) : (<><Lightning size={20} weight="fill" />Build Lead Magnet</>)}
          </button>
        </form>

        <AnimatePresence>
          {result && (
            <>
              <MultiSectionDisplay data={result} onSave={handleSave} isSaved={isSaved} exportMeta={{ title: titleIdea, content_type: `Lead Magnet (${leadMagnetType})`, platform: "Lead Magnet (PDF)", audience, ai_model: aiModel }} resourcesUsed={alwaysIncludeFiles.map(f => f.display_name)} generationContext={{ content_type: `Lead Magnet (${leadMagnetType})`, topic: titleIdea, platform: "Lead Magnet (PDF)", tone: toneVariations.join(", ") }} aiModel={aiModel} />
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-center">
                <button onClick={() => setRepurposeOpen(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-[var(--bg-raised)] border border-[var(--border-strong)] text-[var(--text-secondary)] text-sm font-medium hover:border-[#FF4400]/30 hover:text-[#FF4400] transition-all" data-testid="leadmagnet-repurpose-button">
                  <ArrowsClockwise size={16} />Repurpose This Lead Magnet
                </button>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        <RepurposeModal isOpen={repurposeOpen} onClose={() => setRepurposeOpen(false)} originalContent={result ? result.section_names.map((n) => `### ${n}\n\n${result.sections[n] || ""}`).join("\n\n---\n\n") : ""} originalType="Lead Magnet" originalPlatform="Lead Magnet (PDF)" audience={audience} availableModels={availableModels} defaultTones={toneVariations} defaultModel={aiModel} />
      </main>
    </div>
    <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} trigger="build lead magnet" />
    </NoBusinessGuard>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
        {label} {required && <span className="text-[#FF4400]">*</span>}
      </label>
      {children}
    </div>
  );
}

function ToneSelector({ tones, selected, onToggle, prefix }) {
  return (
    <div>
      <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-3">Tone</label>
      <div className="flex flex-wrap gap-2">
        {tones.map((tone) => (
          <button key={tone} type="button" onClick={() => onToggle(tone)} data-testid={`${prefix}-tone-${tone.toLowerCase()}`}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${selected.includes(tone) ? "bg-[var(--accent-subtle)] border-[#FF4400] text-[#FF4400]" : "bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-secondary)] hover:border-[#FF4400]/30 hover:text-[var(--text-primary)]"}`}
          >{tone}</button>
        ))}
      </div>
    </div>
  );
}

function ModelSelector({ models, value, onChange, prefix }) {
  return (
    <div>
      <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">AI Model <span className="text-[#FF4400]">*</span></label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid={`${prefix}-model-select`}><SelectValue placeholder="Select model" /></SelectTrigger>
        <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
          {models.map((m) => (<SelectItem key={m} value={m} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">{m}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
  );
}
